import { useMemo, useSyncExternalStore } from 'react';

/**
 * Los colores de un gráfico salen del tema activo (spec 310 §3.9), no de hex
 * escritos en cada gráfico: así un ECharts o un Recharts se ve bien en claro y
 * en oscuro sin `isDark ? '#fff' : '#111'` repetido. Se vuelve a calcular cuando
 * cambia la clase `dark` del `<html>`.
 */
export interface TemaDeGrafico {
  oscuro: boolean;
  texto: string;
  textoSuave: string;
  borde: string;
  grilla: string;
  tarjeta: string;
  superficie: string;
  primario: string;
  ok: string;
  baja: string;
  alerta: string;
  bloqueo: string;
  info: string;
  /** Colores para series sin significado propio, en orden. */
  series: string[];
}

const SERIES = ['primary', 'info', 'ia', 'alerta', 'baja', 'ok'];

function suscribirse(avisar: () => void) {
  const observador = new MutationObserver(avisar);
  observador.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => observador.disconnect();
}

const modoActual = () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');

/** `hsl(<valor de --token>)`, con transparencia opcional. */
export function colorDelToken(token: string, alfa?: number): string {
  const valor = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
  if (!valor) return 'currentColor';
  return alfa == null ? `hsl(${valor})` : `hsl(${valor} / ${alfa})`;
}

export function leerTemaDeGrafico(): TemaDeGrafico {
  return {
    oscuro: modoActual() === 'dark',
    texto: colorDelToken('foreground'),
    textoSuave: colorDelToken('muted-foreground'),
    borde: colorDelToken('border'),
    grilla: colorDelToken('border', 0.6),
    tarjeta: colorDelToken('card'),
    superficie: colorDelToken('surface-2'),
    primario: colorDelToken('primary'),
    ok: colorDelToken('ok'),
    baja: colorDelToken('baja'),
    alerta: colorDelToken('alerta'),
    bloqueo: colorDelToken('bloqueo'),
    info: colorDelToken('info'),
    series: SERIES.map((t) => colorDelToken(t)),
  };
}

export function useChartTheme(): TemaDeGrafico {
  const modo = useSyncExternalStore(suscribirse, modoActual, () => 'light');
  // `modo` es la llave: los valores se leen del DOM, que ya cambió de tema.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => leerTemaDeGrafico(), [modo]);
}

/**
 * La base de un `option` de ECharts con el tema: texto, ejes, grilla y tooltip.
 * Se mezcla con lo propio del gráfico (`{ ...baseEcharts(tema), series: … }`).
 */
export function baseEcharts(tema: TemaDeGrafico) {
  const eje = {
    axisLine: { lineStyle: { color: tema.borde } },
    axisTick: { lineStyle: { color: tema.borde } },
    axisLabel: { color: tema.textoSuave },
    splitLine: { lineStyle: { color: tema.grilla } },
    nameTextStyle: { color: tema.textoSuave },
  };
  return {
    color: tema.series,
    backgroundColor: 'transparent',
    textStyle: { color: tema.texto, fontFamily: 'Inter, system-ui, sans-serif' },
    legend: { textStyle: { color: tema.textoSuave } },
    tooltip: {
      backgroundColor: tema.superficie,
      borderColor: tema.borde,
      textStyle: { color: tema.texto },
    },
    xAxis: eje,
    yAxis: eje,
  };
}
